import { activityFetch as fetch } from '../../components/feedback/requestActivity'
import { AsyncFeedback } from '../../components/feedback/LoadingState'
import { titleTheme, pageTheme, headerTheme, contentTheme, subTheme, cardTheme } from '../../components/consoleTheme'

import { useState, useEffect } from "react"
import styled from "styled-components"
import { Plus, Play, Edit3, Trash2, Save, X, Zap, FileCode, Clock, Terminal, RefreshCw } from "lucide-react"

const API = "/api/serverless"
function getToken() { return sessionStorage.getItem("token") || localStorage.getItem("token") }
function authHeaders(json = true) {
  const h = {}
  if (json) h["Content-Type"] = "application/json"
  const t = getToken()
  if (t) h["Authorization"] = `Bearer ${t}`
  return h
}

const Page = styled.div`
  ${pageTheme}
padding:0; background:#f8fafc; min-height:100vh;`
const Header = styled.header`
  ${headerTheme}
display:flex; align-items:center; justify-content:space-between; padding:1rem 2rem; background:#fff; border-bottom:1px solid #e2e8f0;@media(max-width:768px){padding:1rem;flex-wrap:wrap;gap:.75rem;align-items:flex-start;}`
const Title = styled.h1`
 ${titleTheme}
font-size:1.25rem; font-weight:600; color:#1e293b;`
const Sub = styled.p`
  ${subTheme}
margin:.25rem 0 0; font-size:.8rem; color:#94a3b8;`
const Button = styled.button`
  display:inline-flex; align-items:center; gap:.5rem; padding:.5rem 1rem; border-radius:.375rem; font-weight:500; cursor:pointer;
  ${(p) => p.primary ? `background:var(--accent);color:#fff;border:none;&:hover{background:#115855;}` : `background:#fff;color:#64748b;border:1px solid #e2e8f0;&:hover{background:#f8fafc;}`}
  &:disabled{opacity:.5;cursor:default;}`
const Content = styled.div`
  ${contentTheme}
padding:2rem;@media(max-width:768px){padding:1rem;}`
const Card = styled.div`
  ${cardTheme}
background:#fff; border:1px solid #e2e8f0; border-radius:.5rem; overflow:hidden; margin-bottom:1.5rem;`
const CardHead = styled.div`padding:.85rem 1.5rem; border-bottom:1px solid #f1f5f9; font-weight:600; color:#334155; display:flex; align-items:center; justify-content:space-between; gap:.5rem; > span{display:flex;align-items:center;gap:.5rem;}@media(max-width:768px){padding:1rem;flex-wrap:wrap;>span{min-width:0;overflow-wrap:anywhere;}}`
const Empty = styled.div`padding:1.6rem; text-align:center; color:#94a3b8; font-size:.88rem;`
const Mono = styled.code`font-family:ui-monospace,Menlo,Consolas,monospace; font-size:.8rem; color:#334155;`
const Row = styled.div`display:grid; grid-template-columns:${(p) => p.cols || "auto"}; gap:.5rem; align-items:center; padding:.75rem 1.5rem; border-bottom:1px solid #f1f5f9; font-size:.86rem; &:last-child{border-bottom:none;}@media(max-width:768px){grid-template-columns:minmax(0,1fr) minmax(0,1fr);padding:1rem;gap:.65rem;border-bottom:8px solid #f8fafc;>*{min-width:0;overflow-wrap:anywhere;}>:first-child,>:last-child{grid-column:1/-1;}}`
const IconBtn = styled.button`background:none; border:1px solid #e2e8f0; border-radius:6px; padding:4px 8px; cursor:pointer; color:#64748b; display:inline-flex; align-items:center; gap:4px; font-size:.78rem; &:hover{color:var(--accent);border-color:var(--accent);}`
const Input = styled.input`padding:.5rem .7rem; border:1px solid #e2e8f0; border-radius:6px; outline:none; font-size:.85rem; &:focus{border-color:var(--accent);}`
const Select = styled.select`padding:.5rem .7rem; border:1px solid #e2e8f0; border-radius:6px; outline:none; font-size:.85rem; background:#fff;`
const TextArea = styled.textarea`width:100%; min-height:300px; padding:.8rem; border:1px solid #e2e8f0; border-radius:6px; outline:none; font-family:ui-monospace,Menlo,Consolas,monospace; font-size:.82rem; line-height:1.5; resize:vertical; &:focus{border-color:var(--accent);}`
const Pre = styled.pre`background:#0f172a; color:#e2e8f0; border-radius:8px; padding:1rem; margin:0; overflow:auto; max-height:360px; font-family:ui-monospace,Menlo,Consolas,monospace; font-size:.78rem; line-height:1.5; white-space:pre-wrap; word-break:break-word;`
const Badge = styled.span`display:inline-block; padding:1px 8px; border-radius:999px; font-size:.72rem; font-weight:600;
  ${(p) => p.s === "success" ? "background:#dcfce7;color:#166534;" : p.s === "failed" ? "background:#fee2e2;color:#991b1b;" : "background:#dbeafe;color:#1e40af;"}`
const Note = styled.div`display:flex; gap:.6rem; padding:.85rem 1rem; border-radius:8px; font-size:.84rem; line-height:1.55; background:#eff6ff; border:1px solid #bfdbfe; color:#1e40af; margin-bottom:1.25rem;`
const RT_LABEL = { nodejs: "Node.js 20", python: "Python 3.12" }

const Serverless = () => {
  const [fns, setFns] = useState([])
  const [nodes, setNodes] = useState([])
  const [busy, setBusy] = useState("")
  const [editor, setEditor] = useState(null)   // {fn_id?, name, runtime, code, target_node_id}
  const [adding, setAdding] = useState(false)
  const [newFn, setNewFn] = useState({ name: "", runtime: "nodejs", target_node_id: "" })
  const [invoke, setInvoke] = useState(null)    // {fn, eventText, result}
  const [invs, setInvs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true); setError('')
    try {
      const responses = await Promise.all([fetch(`${API}/functions`, { headers: authHeaders(false), credentials: "include" }), fetch('/api/storage/nodes', { headers: authHeaders(false), credentials: "include" })])
      if (responses.some(r => !r.ok)) throw Error('함수 또는 실행 노드를 조회하지 못했습니다. 다시 조회해 주세요.')
      const [d,n] = await Promise.all(responses.map(r => r.json()))
      if (!Array.isArray(d.functions) || !Array.isArray(n.nodes)) throw Error('서버 응답을 확인할 수 없습니다.')
      setFns(d.functions); setNodes(n.nodes)
    } catch (e) { setError(e.message || '조회에 실패했습니다.') }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const create = async () => {
    if (busy) return
    if (!newFn.name) { alert("이름 필수"); return }
    setBusy("new")
    try {
      const res = await fetch(`${API}/functions`, { method: "POST", headers: authHeaders(), credentials: "include", body: JSON.stringify(newFn) })
      if (!res.ok) { alert("생성 실패"); return }
      setNewFn({ name: "", runtime: "nodejs", target_node_id: "" }); setAdding(false); load()
    } catch (e) { setError(e.message || '함수를 생성하지 못했습니다.') } finally { setBusy("") }
  }
  const save = async () => {
    if (busy) return
    setBusy("save")
    try {
      const response = await fetch(`${API}/functions/${editor.fn_id}`, { method: "PUT", headers: authHeaders(), credentials: "include", body: JSON.stringify({ name: editor.name, runtime: editor.runtime, code: editor.code, target_node_id: editor.target_node_id ? Number(editor.target_node_id) : null }) })
      if (!response.ok) throw Error('함수 저장에 실패했습니다. 입력 내용은 유지됩니다.')
      setEditor(null); load()
    } catch (e) { setError(e.message || '저장하지 못했습니다.') } finally { setBusy("") }
  }
  const del = async (id) => {
    if (busy || !window.confirm("함수를 삭제할까요?")) return
    setBusy('delete'); setError('')
    try {
      const response=await fetch(`${API}/functions/${id}`, { method: "DELETE", headers: authHeaders(false), credentials: "include" })
      if (!response.ok) throw Error('함수를 삭제하지 못했습니다.')
      await load()
    } catch(e) { setError(e.message || '삭제 요청을 확인하지 못했습니다.') }
    finally { setBusy('') }
  }

  const openInvoke = async (fn) => {
    setInvoke({ fn, eventText: '{\n  "name": "world"\n}', result: null })
    try { const d = await fetch(`${API}/functions/${fn.fn_id}/invocations`, { headers: authHeaders(false), credentials: "include" }).then(x => x.json()); setInvs(d.invocations || []) } catch (_) { setInvs([]) }
  }
  const runInvoke = async () => {
    if (busy) return
    let event = {}
    try { event = invoke.eventText.trim() ? JSON.parse(invoke.eventText) : {} } catch (e) { alert("이벤트 JSON 형식 오류"); return }
    setBusy("invoke"); setInvoke(v => ({ ...v, result: { status: "running", logs: "실행 중…" } }))
    try {
      const res = await fetch(`${API}/functions/${invoke.fn.fn_id}/invoke`, { method: "POST", headers: authHeaders(), credentials: "include", body: JSON.stringify({ event }) })
      const d = await res.json().catch(() => ({}))
      if (!res.ok) { setInvoke(v => ({ ...v, result: { status: "failed", logs: d.error || "실행 실패" } })); return }
      setInvoke(v => ({ ...v, result: d }))
      const inv = await fetch(`${API}/functions/${invoke.fn.fn_id}/invocations`, { headers: authHeaders(false), credentials: "include" }).then(x => x.json()); setInvs(inv.invocations || [])
      load()
    } catch (e) { setInvoke(v => ({ ...v, result: { status: "failed", logs: e.message } })) }
    finally { setBusy("") }
  }

  // ── 에디터 화면
  if (editor) {
    return (
      <Page>
        <Header><div><Title>함수 편집</Title><Sub>{editor.name}</Sub></div>
          <div style={{ display: "flex", gap: ".5rem" }}><Button onClick={() => setEditor(null)}><X size={16} /> 취소</Button><Button primary onClick={save} disabled={busy === "save"}><Save size={16} /> 저장</Button></div>
        </Header>
        <Content>
          <AsyncFeedback busy={Boolean(busy) || loading} error={error} onRetry={load} initial={loading && !fns.length} label={busy ? '요청을 처리하고 있습니다' : '함수와 실행 노드를 불러오고 있습니다'} />
          <Card><div style={{ padding: "1rem 1.5rem" }}>
            <div style={{ display: "flex", gap: ".5rem", marginBottom: ".7rem" }}>
              <Input value={editor.name} onChange={e => setEditor({ ...editor, name: e.target.value })} style={{ width: 280 }} />
              <Select value={editor.runtime} onChange={e => setEditor({ ...editor, runtime: e.target.value })}>
                <option value="nodejs">Node.js 20</option><option value="python">Python 3.12</option>
              </Select>
              <Select value={editor.target_node_id || ""} onChange={e => setEditor({ ...editor, target_node_id: e.target.value })} title="실행 위치">
                <option value="">중앙 서버</option>
                {nodes.map(n => <option key={n.node_id} value={n.node_id}>온프레: {n.name}</option>)}
              </Select>
            </div>
            <TextArea value={editor.code} onChange={e => setEditor({ ...editor, code: e.target.value })} spellCheck={false} />
            <p style={{ color: "#94a3b8", fontSize: ".78rem", marginTop: ".5rem" }}>
              {editor.runtime === "python"
                ? <>핸들러: <Mono>def handler(event):</Mono> — 반환값이 결과가 됩니다.</>
                : <>핸들러: <Mono>exports.handler = async (event) =&gt; {"{...}"}</Mono> — 반환값이 결과가 됩니다.</>}
            </p>
          </div></Card>
        </Content>
      </Page>
    )
  }

  // ── 호출 화면
  if (invoke) {
    const r = invoke.result
    return (
      <Page>
        <Header><div><Title>함수 호출 — {invoke.fn.name}</Title><Sub>{RT_LABEL[invoke.fn.runtime]}</Sub></div>
          <Button onClick={() => setInvoke(null)}><X size={16} /> 닫기</Button>
        </Header>
        <Content>
          <AsyncFeedback busy={Boolean(busy) || loading} error={error} onRetry={load} initial={loading && !fns.length} label={busy ? '요청을 처리하고 있습니다' : '함수와 실행 노드를 불러오고 있습니다'} />
          <Card>
            <CardHead><span><Play size={16} /> 이벤트(JSON) 전달 후 실행</span></CardHead>
            <div style={{ padding: "1rem 1.5rem" }}>
              <TextArea style={{ minHeight: 120 }} value={invoke.eventText} onChange={e => setInvoke({ ...invoke, eventText: e.target.value })} spellCheck={false} />
              <Button primary onClick={runInvoke} disabled={busy === "invoke"} style={{ marginTop: ".6rem" }}><Play size={15} /> {busy === "invoke" ? "실행 중…" : "실행"}</Button>
              {r && (
                <div style={{ marginTop: "1rem" }}>
                  <div style={{ marginBottom: ".4rem" }}><Badge s={r.status}>{r.status}</Badge>
                    {r.duration_ms != null && <span style={{ color: "#94a3b8", fontSize: ".8rem", marginLeft: 8 }}>{r.duration_ms}ms · exit {r.exit_code}{r.target ? ` · ${r.target === "central" ? "중앙 서버" : r.target}` : ""}</span>}</div>
                  {r.result !== undefined && r.result !== null && (
                    <><div style={{ fontSize: ".8rem", color: "#475569", margin: ".3rem 0" }}>반환값</div>
                      <Pre style={{ background: "#0b3d1e" }}>{typeof r.result === "string" ? r.result : JSON.stringify(r.result, null, 2)}</Pre></>
                  )}
                  <div style={{ fontSize: ".8rem", color: "#475569", margin: ".5rem 0 .3rem" }}>로그</div>
                  <Pre>{r.logs || "(없음)"}</Pre>
                </div>
              )}
            </div>
          </Card>
          {invs.length > 0 && (
            <Card>
              <CardHead><span><Clock size={16} /> 최근 호출</span></CardHead>
              {invs.map(i => (
                <Row key={i.inv_id} cols="auto 1fr 1fr">
                  <Badge s={i.status}>{i.status}</Badge>
                  <span style={{ color: "#64748b", fontSize: ".82rem" }}>{i.duration_ms != null ? `${i.duration_ms}ms` : "-"} · exit {i.exit_code}</span>
                  <span style={{ color: "#94a3b8", fontSize: ".78rem", textAlign: "right" }}>{new Date(i.created_at).toLocaleString()}</span>
                </Row>
              ))}
            </Card>
          )}
        </Content>
      </Page>
    )
  }

  // ── 목록
  return (
    <Page>
      <Header>
        <div><Title>서버리스</Title><Sub>함수를 작성하고 Podman 컨테이너에서 격리 실행합니다.</Sub></div>
        <div style={{ display: "flex", gap: ".5rem" }}>
          <Button onClick={load} disabled={loading || Boolean(busy)}><RefreshCw size={16} /> 새로고침</Button>
          <Button primary onClick={() => setAdding(v => !v)}><Plus size={16} /> 함수 생성</Button>
        </div>
      </Header>
      <Content>
          <AsyncFeedback busy={Boolean(busy) || loading} error={error} onRetry={load} initial={loading && !fns.length} label={busy ? '요청을 처리하고 있습니다' : '함수와 실행 노드를 불러오고 있습니다'} />
        <Note><Zap size={18} style={{ flexShrink: 0 }} />
          <span>함수는 <strong>Node.js / Python</strong>로 작성하며, 호출 시 격리 컨테이너(메모리 256MB·CPU 1·30초 제한)에서 실행됩니다.
            핸들러의 반환값이 결과로 반환됩니다.</span>
        </Note>
        <Card>
          <CardHead><span><FileCode size={16} /> 함수 ({loading || error ? '—' : fns.length})</span></CardHead>
          {adding && (
            <div style={{ display: "flex", gap: ".5rem", padding: "1rem 1.5rem", borderBottom: "1px solid #f1f5f9", alignItems: "center" }}>
              <Input placeholder="함수 이름" value={newFn.name} onChange={e => setNewFn({ ...newFn, name: e.target.value })} />
              <Select value={newFn.runtime} onChange={e => setNewFn({ ...newFn, runtime: e.target.value })}>
                <option value="nodejs">Node.js 20</option><option value="python">Python 3.12</option>
              </Select>
              <Select value={newFn.target_node_id} onChange={e => setNewFn({ ...newFn, target_node_id: e.target.value })} title="실행 위치">
                <option value="">중앙 서버</option>
                {nodes.map(n => <option key={n.node_id} value={n.node_id}>온프레: {n.name}</option>)}
              </Select>
              <Button primary onClick={create} disabled={busy === "new"}>생성</Button>
            </div>
          )}
          {fns.length === 0 ? (!loading && !error && <Empty>함수가 없습니다. “함수 생성”으로 첫 함수를 만들어 보세요.</Empty>) : fns.map(f => (
            <Row key={f.fn_id} cols="1.5fr 1fr .9fr auto">
              <span style={{ fontWeight: 600, color: "#1e293b", display: "flex", alignItems: "center", gap: 6 }}><Zap size={15} color="var(--accent)" /> {f.name}</span>
              <span style={{ color: "#64748b" }}>{RT_LABEL[f.runtime] || f.runtime}
                <div style={{ fontSize: ".74rem", color: f.target_node_id ? "#ea580c" : "#94a3b8" }}>{f.target_node_id ? `온프레: ${f.target_node_name || f.target_node_id}` : "중앙 서버"}</div>
              </span>
              <span style={{ color: "#94a3b8", fontSize: ".82rem" }}>호출 {f.invocations}</span>
              <span style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                <IconBtn disabled={Boolean(busy) || loading} onClick={() => openInvoke(f)} style={{ borderColor: "#bfdbfe", color: "var(--accent)" }}><Play size={13} /> 실행</IconBtn>
                <IconBtn disabled={Boolean(busy) || loading} onClick={() => setEditor({ fn_id: f.fn_id, name: f.name, runtime: f.runtime, code: f.code, target_node_id: f.target_node_id || "" })}><Edit3 size={13} /> 편집</IconBtn>
                <IconBtn disabled={Boolean(busy) || loading} aria-label={`${f.name} 삭제`} style={{ borderColor: "#fecaca", color: "#ef4444" }} onClick={() => del(f.fn_id)}><Trash2 size={13} /></IconBtn>
              </span>
            </Row>
          ))}
        </Card>
      </Content>
    </Page>
  )
}

export default Serverless
